import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { debounce, Grid2 as Grid, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { useUsers } from '../../../hooks/useUsers';
import { SeeButton, EditButton } from '../../../component/buttons';
import SkeletonEarningCard from '../../../component/cards/Skeleton/EarningCard';
import { useRole } from '../../../utils/RoleContext';

const RendEditCell = (params) => {
    const userRow = params.row;
    const navigate = useNavigate();
    const editText = t('users.edit');
    const seeText = t('users.see');

    return (
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            <SeeButton
                onClick={() => navigate('/users/user', { state: { user: userRow } })}
                size="small"
            >
                <Typography variant="subtitle2">{seeText}</Typography>
            </SeeButton>

            <EditButton
                onClick={() => navigate('/users/user/edit', { state: { user: userRow } })}
                size="small"
            >
                <Typography variant="subtitle2">{editText}</Typography>
            </EditButton>
        </Grid>
    );
};

const RendEditOnlyCell = (params) => {
    const userRow = params.row;
    const navigate = useNavigate();
    return (
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            <EditButton
                onClick={() => navigate('/users/user/edit', { state: { user: userRow } })}
                size="small"
            />
        </Grid>
    );
};

function UsersTable({ types, typeName }) {
    const { role } = useRole();
    const { t } = useTranslation();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        setPaginationModel((prev) => ({
            ...prev,
            page: 0
        }));
    }, [search]);

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value);
            }, 1500),
        []
    );

    const {
        data,
        isLoading,
        rowsPerPageOptions,
        count,
        paginationModel,
        setPaginationModel
    } = useUsers(types, search);

    const tableConfig = (typeName, types, role) => {
        if (role === 'CLINIC_ADMIN') {
            return {
                columns: [
                    {
                        field: 'name',
                        headerName: typeName,
                        flex: 1
                    },
                    {
                        field: 'edit',
                        headerName: t('tableHeaders.actions'),
                        headerAlign: 'center',
                        display: 'flex',
                        align: 'center',
                        width: 250,
                        renderCell: RendEditOnlyCell
                    }
                ]
            };
        }

        const baseColumns = [
            {
                field: 'name',
                headerName: typeName,
                flex: 1
            }
        ];

        if (!types.includes('DOCTOR')) {
            baseColumns.push({
                field: 'edit',
                headerName: t('tableHeaders.actions'),
                headerAlign: 'center',
                display: 'flex',
                align: 'center',
                width: 250,
                renderCell: RendEditCell
            });
        }

        return { columns: baseColumns };
    };

    return (
        <div>
            {isLoading ? (
                <SkeletonEarningCard />
            ) : (
                <>
                    <TextField
                        label={t('users.searchUser')}
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            debouncedSearch(e.target.value);
                        }}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <DataGrid
                        style={{ width: '100%' }}
                        density="standard"
                        loading={isLoading}
                        rows={data}
                        getRowId={(row) => row.user_uuid}
                        columns={tableConfig(typeName, types, role).columns}
                        paginationMode="server"
                        pageSizeOptions={rowsPerPageOptions}
                        rowCount={count}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                    />
                </>
            )}
        </div>
    );
}

UsersTable.propTypes = {
    typeName: PropTypes.string,
    types: PropTypes.array
};

export default UsersTable;
